<template>
    <div class="student-result-tertiary">
        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-12">
            <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
            <i class="pi pi-exclamation-circle text-red-500 text-xl mb-2"></i>
            <p class="text-red-700">{{ error.message || 'Failed to load student result' }}</p>
            <Button label="Retry" icon="pi pi-refresh" class="mt-4" @click="refetchResult" severity="secondary" data-testid="btn-results-tertiary-retry" />
        </div>

        <!-- No Result State -->
        <div v-else-if="!studentResult" class="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <i class="pi pi-info-circle text-gray-400 text-xl mb-2"></i>
            <p class="text-gray-600">No result found for this student.</p>
        </div>

        <!-- Result Content -->
        <div v-else class="space-y-3">
            <!-- Result Header -->
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                <div class="flex flex-col md:flex-row justify-between">
                    <!-- Student Info -->
                    <div class="flex items-start gap-3">
                        <div class="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                            <i class="pi pi-user text-lg"></i>
                        </div>
                        <div>
                            <h2 class="text-lg font-bold text-gray-900">{{ studentFullName }}</h2>
                            <p class="text-gray-600">
                                <span class="font-medium">Matric No:</span> {{ student?.matric_no }}
                            </p>
                            <p v-if="student?.email" class="text-gray-500 text-sm">{{ student.email }}</p>
                        </div>
                    </div>

                    <!-- Programme Info -->
                    <div class="mt-2 md:mt-0 text-right">
                        <h3 class="text-base font-semibold text-primary-800">{{ programme?.program_name }}</h3>
                        <p class="text-gray-600">
                            <span class="font-medium">{{ programme?.prog_code }}</span>
                            <span v-if="programme?.degree" class="ml-2 text-sm bg-primary/10 text-primary-700 px-2 py-0.5 rounded">
                                {{ programme.degree }}
                            </span>
                        </p>
                        <p class="text-sm text-gray-500 mt-1">
                            {{ academicCalendarLabel }}
                        </p>
                    </div>
                </div>

                <!-- Publication Status -->
                <div class="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                    <div class="flex items-center gap-3">
                        <span :class="statusBadgeClass">
                            {{ status === 'PUBLISHED' ? 'Published' : 'Not Published' }}
                        </span>
                        <Button v-if="status === 'PUBLISHED'" label="Unpublish" icon="pi pi-undo"
                            size="small" severity="danger" outlined :loading="unpublishing"
                            @click="handleUnpublish" />
                    </div>
                    <div v-if="template" class="text-sm text-gray-500">
                        Template: <span class="font-medium">{{ template.name }}</span>
                    </div>
                </div>
            </div>

            <!-- Result Table -->
            <div v-for="group in moduleResultGroups" :key="group.key" class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <p v-if="group.label" class="p-3 font-semibold">{{ group.label }}</p>
                <DataTable :value="group.rows" tableStyle="min-width: 50rem" stripedRows size="small">
                    <Column v-if="!hasCourseColumns" field="subject_code" header="Code" style="width: 15%">
                        <template #body="slotProps">
                            <div v-if="slotProps.data.is_transferred" class="flex items-center gap-2">
                                <span>{{ slotProps.data.subject_code }}</span>
                                <span class="transfer-badge" :title="'Transferred from ' + slotProps.data.original_programme">
                                    T
                                </span>
                            </div>
                            <span v-else>{{ slotProps.data.subject_code }}</span>
                        </template>
                    </Column>
                    <Column v-if="!group.label && resultData.some(r => r.module_name)" field="module_name" header="Module" />
                    <Column v-if="!hasCourseColumns" field="subject_name" header="Course" style="width: 25%"></Column>
                    <Column v-for="header in visibleHeaders" :key="header.key" :field="header.key" :header="header.label">
                        <template #body="slotProps">
                            <template v-if="header.key === 'course_code' && slotProps.data.is_transferred">
                                <div class="flex items-center gap-2">
                                    <span>{{ slotProps.data[header.key] }}</span>
                                    <span class="transfer-badge" :title="'Transferred from ' + slotProps.data.original_programme">
                                        T
                                    </span>
                                </div>
                            </template>
                            <template v-else>
                                {{ formatValue(slotProps.data[header.key], header.type) }}
                            </template>
                        </template>
                    </Column>

                    <template #empty>
                        <div class="text-center py-4 text-gray-500">No course results available</div>
                    </template>
                </DataTable>
            </div>

            <!-- GPA Summary -->
            <div class="bg-gradient-to-r from-primary/10 to-indigo-50 rounded-lg shadow-sm border border-primary/20 p-3">
                <h3 class="text-base font-semibold text-gray-800 mb-2">Academic Summary</h3>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div class="bg-white rounded-lg p-2.5 shadow-sm border border-gray-100">
                        <p class="text-sm text-gray-500">{{ summary?.total_units?.label || 'Total Units' }}</p>
                        <p class="text-xl font-bold text-gray-900">{{ summary?.total_units?.value ?? '-' }}</p>
                    </div>
                    <div class="bg-white rounded-lg p-2.5 shadow-sm border border-gray-100">
                        <p class="text-sm text-gray-500">{{ summary?.total_weighted_gp?.label || 'Total Weighted GP' }}</p>
                        <p class="text-xl font-bold text-gray-900">
                            {{ formatNumber(summary?.total_weighted_gp?.value) }}
                        </p>
                    </div>
                    <div class="rounded-lg p-2.5 shadow-sm border border-primary/20 bg-primary/10">
                        <p class="text-sm text-primary font-medium">{{ summary?.gpa?.label || 'GPA' }}</p>
                        <p class="text-xl font-bold text-primary-800">{{ formatNumber(summary?.gpa?.value) }}</p>
                    </div>
                    <div class="rounded-lg p-2.5 shadow-sm border border-indigo-200 bg-indigo-50">
                        <p class="text-sm text-indigo-600 font-medium">{{ summary?.cgpa?.label || 'CGPA' }}</p>
                        <p class="text-xl font-bold text-indigo-900">{{ formatNumber(summary?.cgpa?.value) }}</p>
                        <p v-if="cgpaClassification" class="text-xs text-indigo-600 mt-1">{{ cgpaClassification }}</p>
                    </div>
                </div>
            </div>

            <!-- Feedbacks Section -->
            <div v-if="feedbacks && feedbacks.length > 0" class="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                <h3 class="text-base font-semibold text-gray-800 mb-2">Remarks & Feedback</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div v-for="fb in feedbacks" :key="fb.id" class="border border-gray-100 rounded-md p-3">
                        <p class="text-sm text-gray-500">{{ fb.result_template_feedback?.label || 'Feedback' }}</p>
                        <p class="font-medium text-gray-800">{{ feedbackDisplayText(fb.result_template_feedback, fb) }}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import gql from 'graphql-tag';
import { useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { useStudentResultTertiary } from '@/composables/queries/useProgrammeResultTemplate';
import { errorMessageOf } from '@/utils/errorMessage';
import { feedbackDisplayText } from '@/utils/resultFeedbackRating';

interface TertiaryResultSummary {
    total_units?: { value: number; label: string };
    total_weighted_gp?: { value: number; label: string };
    gpa?: { value: number; label: string };
    cgpa?: { value: number; label: string };
}

interface ResultHeader {
    label: string;
    key: string;
    type: string;
    is_visible: boolean;
}

const props = defineProps<{
    studentId: string;
    academicCalendarId: string;
}>();

const emit = defineEmits<{
    loaded: [result: unknown];
}>();

const {
  studentResult,
  headers,
  data,
  summary: rawSummary,
  status,
  student,
  programme,
  template,
  feedbacks,
  loading,
  error,
  fetchResult,
} = useStudentResultTertiary({
  studentId: props.studentId,
  academicCalendarId: props.academicCalendarId,
  autoFetch: true,
});

// Watch for prop changes and refetch
watch(
  () => [props.studentId, props.academicCalendarId],
  ([newStudentId, newCalendarId]) => {
    if (newStudentId && newCalendarId) {
      fetchResult(newStudentId, newCalendarId);
    }
  },
);

// Computed
const studentFullName = computed(() => {
  if (!student.value) return '';
  const parts = [student.value.surname, student.value.first_name, student.value.other_name].filter(Boolean);
  return parts.join(' ');
});

const academicCalendarLabel = computed(() => {
  const cal = studentResult.value?.academic_calendar;
  if (!cal) return '';
  return `${cal.session?.name || ''} - ${cal.termStructure?.name || ''}`;
});

const visibleHeaders = computed<ResultHeader[]>(() => (headers.value as ResultHeader[]).filter((h) => h.is_visible !== false));

const courseKeys = ['course_code', 'course_title', 'subject_code', 'subject_name'];
const hasCourseColumns = computed(() => visibleHeaders.value.some((h) => courseKeys.includes(h.key)));

const resultData = computed(() => Array.isArray(data.value) ? data.value : []);
const moduleResultGroups = computed(() => {
  if (!resultData.value.some(row => row.module_result_mode === 'separate')) return [{ key: 'semester', label: '', rows: resultData.value }];
  const groups = new Map<string, { key: string; label: string; rows: typeof resultData.value }>();
  for (const row of resultData.value) {
    const key = String(row.programme_module_id ?? 'unassigned');
    if (!groups.has(key)) groups.set(key, { key, label: row.module_name || 'Unassigned / historical courses', rows: [] });
    groups.get(key)!.rows.push(row);
  }
  return [...groups.values()];
});

const summary = computed<TertiaryResultSummary>(() => (rawSummary.value as TertiaryResultSummary) || {});

const statusBadgeClass = computed(() => status.value === 'PUBLISHED'
  ? 'px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary'
  : 'px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800');

const cgpaClassification = computed(() => {
  const cgpa = summary.value?.cgpa?.value;
  if (cgpa === undefined || cgpa === null) return '';
  return getGPAClassification(cgpa);
});

// Functions
function getGPAClassification(cgpa: number): string {
  if (cgpa >= 4.50) return 'First Class Honours';
  if (cgpa >= 3.50) return 'Second Class Honours (Upper Division)';
  if (cgpa >= 2.40) return 'Second Class Honours (Lower Division)';
  if (cgpa >= 1.50) return 'Third Class Honours';
  if (cgpa >= 1.00) return 'Pass';
  return 'Fail';
}

// Unpublish
const toast = useToast();
const unpublishing = ref(false);

const UNPUBLISH_RESULT = gql`
  mutation UnPublishStudentResult($academic_calendar_id: ID!, $student_id: ID!) {
    unPublishStudentResult(academic_calendar_id: $academic_calendar_id, student_id: $student_id) {
      id
      status
    }
  }
`;
const { mutate: unpublishMutate } = useMutation(UNPUBLISH_RESULT);

async function handleUnpublish() {
  unpublishing.value = true;
  try {
    await unpublishMutate({
      student_id: props.studentId,
      academic_calendar_id: props.academicCalendarId,
    });
    toast.add({ severity: 'success', summary: 'Result unpublished', detail: 'The published result has been removed.', life: 3000 });
    fetchResult(props.studentId, props.academicCalendarId);
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Unpublish failed', detail: errorMessageOf(e, 'Could not unpublish result.'), life: 4000 });
  } finally {
    unpublishing.value = false;
  }
}

function refetchResult() {
  fetchResult(props.studentId, props.academicCalendarId);
}

function formatNumber(value: number | undefined | null): string {
  if (value === undefined || value === null) return '-';
  return value.toFixed(2);
}

function formatValue(value: unknown, type: string): string {
  if (value === undefined || value === null) return '-';

  if (type === 'GRADE_POINT' || type === 'WEIGHTED_GRADE_POINT' || type === 'CALCULATION') {
    const num = Number(value);
    return isNaN(num) ? String(value) : num.toFixed(2);
  }

  return String(value);
}

// Emit loaded event when result is available
watch(studentResult, (result) => {
  if (result) {
    emit('loaded', result);
  }
});
</script>

<style scoped>
.transfer-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    background-color: #3b82f6;
    color: white;
    border-radius: 50%;
    font-size: 10px;
    font-weight: bold;
    cursor: help;
}

.transferred-course {
    background-color: #f0f9ff;
}
</style>
